import { Formik } from 'formik';
import React, { useMemo } from 'react';
import { t } from 'ttag';
import * as Yup from 'yup';
import config from '../../../../config/channels';
import { CustomerFormObject } from '../../../../contexts/customer/customer-context';
import useCustomerContext from '../../../../contexts/customer/useCustomerContext';
import useSiteContext from '../../../../contexts/site/useSiteContext';
import PaymentForm from './PaymentForm';
import {iLog} from "../../../../index";
import {useLocalStorage} from "../../../../hooks/localstorage";
import useOperatorContext from "../../../../contexts/operator/useOperatorContext";

export type FormData = {
	companyName?: boolean;
    firstName?: boolean;
    lastName?: boolean;
    cardholderName?: boolean;
    email?: boolean;
    phone?: boolean;
    address?: boolean;
    table_select?: boolean;
    show_instructions?: boolean;
    opt_in_marketing?: boolean;
};

interface FormBuilderProps {
    form: FormData;
}

// RegEx for phone number validation
const phoneRegExp = /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/;

const validationSchema = {
    firstName: Yup.string()
        .max(100, t`First name can't be longer than 100 characters`)
        .required(t`Enter a first name`),

    lastName: Yup.string()
        .min(2, t`Last name must have at least 2 characters`)
        .max(100, t`Last name can't be longer than 100 characters`)
        .required(t`Enter a last name`),

    email: Yup.string()
        .email(t`Must be a valid email address`)
        .max(100, t`Email must be less than 100 characters`)
        .required(t`Enter an email`),

    opt_in_marketing: Yup.boolean(),
    checkout_instructions: Yup.string()
    	.max(100, t`Instructions must be less than 100 characters`),
};

// Schema for yup
const validationSchemaCollection = Yup.object().shape({
    ...validationSchema,

    tel: Yup.string()
        .matches(phoneRegExp, t`Phone number is not valid`)
        .required(t`Enter a phone number`),
});

const validationSchemaDineIn = Yup.object().shape({
    ...validationSchema,
});

const validationSchemaDelivery = Yup.object().shape({
    ...validationSchema,

    tel: Yup.string()
        .matches(phoneRegExp, t`Phone number is not valid`)
        .required(t`Enter a phone number`),

    address1: Yup.string().required(t`Enter an address`),
    city: Yup.string().required(t`Enter a city or town`),
    postcode: Yup.string().required(t`Enter a postcode`),
});

const validationSchemaCatering = Yup.object().shape({
    ...validationSchema,

    companyName: Yup.string()
        .max(100, t`Company name can't be longer than 100 characters`),

    tel: Yup.string()
        .matches(phoneRegExp, t`Phone number is not valid`)
        .required(t`Enter a phone number`),

    address1: Yup.string().required(t`Enter an address`),
    city: Yup.string().required(t`Enter a city or town`),
    postcode: Yup.string().required(t`Enter a postcode`),
});

const validationSchemaVenues = Yup.object().shape({
    ...validationSchema,

    tel: Yup.string()
        .matches(phoneRegExp, t`Phone number is not valid`)
        .required(t`Enter a phone number`),

    address1: Yup.string().required(t`Enter an address`),
    city: Yup.string().required(t`Enter a city or town`),
    postcode: Yup.string().required(t`Enter a postcode`),
});

const FormBuilder = ({ form }: FormBuilderProps) => {
    const customerContext = useCustomerContext();
    const operatorContext = useOperatorContext();
    const siteContext = useSiteContext();

    const address = JSON.parse(localStorage.getItem('address') ?? '{}');
    const user = JSON.parse(localStorage.getItem('user') ?? '{}');

    const initialValues = useMemo<CustomerFormObject>(() => ({
        ...customerContext.customerFormData,
        delivery_venue_name: address.deliveryVenueName ?? '',
        address1: address.address1 ?? '',
        address2: address.address2 ?? '',
        city: address.city ?? '',
        postcode: localStorage.getItem('postcode') ?? customerContext.customerFormData.postcode,
        opt_in_marketing: operatorContext.operator?.theme.v4.opt_in_default ?? false,

		companyName: user.companyName ?? '',
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        tel: user.tel ?? '',
        email: user.email ?? '',
        remember_me: !!(user.remember_me ?? (operatorContext.operator?.theme.v4.remember_me && operatorContext.operator?.theme.v4.remember_me_default))
    }), [])

    const schema = () => {
        switch (siteContext.channel) {
            case config.delivery.name:
                return validationSchemaDelivery;

            case config.catering.name:
                return validationSchemaCatering;

            case config.venues.name:
                return validationSchemaVenues;

            case config.dineIn.name:
                return validationSchemaDineIn;

            default:
                return validationSchemaCollection;
        }
    };

    return (
        <>
            <Formik
                //enableReinitialize
                initialValues={initialValues}
                isInitialValid={() => schema().isValidSync(initialValues)}
                validationSchema={schema()}
                onSubmit={(values, { setSubmitting, resetForm, validateForm }) => {

                    // When button submits form and form is in the process of submitting, submit button is disabled
                    setSubmitting(true);

                    iLog('form values', values);

                    validateForm().then(() => {
                        // Resets form after submission is complete
                        // customerContext.setCustomerFormData(values);
                        customerContext.updateStoredCustomer(values);
                        // resetForm();
                    });

                    // Sets setSubmitting to false after form is reset
                    setSubmitting(false);
                }}
            >
                {({ values, errors, touched, isValid, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
                    <>
                        <PaymentForm form={form} />
                    </>
                )}
            </Formik>
        </>
    );
};

export default FormBuilder;
